import React, { useState, useRef } from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { Fade } from "@mui/material";
import useApp from "../../store/contexts/AppContext";
import capitalizeEachFirstWord from "../../utils/CapLetters";
import "./styles.css"; // Import your CSS file for styling transitions

export default function RoomParticipantList({
  classes,
  roomParticipants,
  roomLoading,
  roomModerators,
  roomId,
}) {
  const {
    appState: { userData, mutedActiveRoomParticipantIds },
    adminUpdateUserBanStatusApiCall,
    adminUpdateUserToolboxBanStatusApiCall,
    userKickOffParticipantVoteApiCall,
    adminUpdateUserModeratorshipStatusApiCall,
  } = useApp();
  const userId = userData?.id; // Added a safe navigation operator in case userData is undefined

  const [anchorAdminEl, setAnchorAdminEl] = useState(null);
  const [anchorUserEl, setAnchorUserEl] = useState(null);
  const participantRefs = useRef([]);

  // Check if current user is a room moderator
  const checkCurrentUserIsRoomModerator = roomModerators.find(
    (moderator) => moderator.id?.toString() === userId?.toString()
  );

  const currentUserIsAdmin = checkCurrentUserIsRoomModerator ? true : false;

  const handleAdminClick = (event) => {
    handleUserClose();
    setAnchorAdminEl(event.currentTarget);
  };

  const handleAdminClose = () => {
    setAnchorAdminEl(null);
  };

  const handleUserClick = (event) => {
    handleAdminClose();
    setAnchorUserEl(event.currentTarget);
  };

  const handleUserClose = () => {
    setAnchorUserEl(null);
  };

  const sharedMenuPaperProps = {
    sx: {
      background: "linear-gradient(135deg, #111111 0%, #1d1d1d 100%)",
      color: "#ffffff",
      border: "1px solid rgba(255, 107, 53, 0.24)",
      borderRadius: "10px",
      boxShadow: "0 10px 28px rgba(0, 0, 0, 0.28)",
      "& .MuiMenuItem-root": {
        fontSize: "13px",
        "&:hover": {
          backgroundColor: "rgba(255, 107, 53, 0.14)",
        },
      },
    },
  };

  const handleBanStatusUpdate = (participantId) => {
    const dataToPass = {
      participant_id: participantId,
    };
    adminUpdateUserBanStatusApiCall(
      dataToPass,
      roomModerators,
      roomId,
      currentUserIsAdmin
    );
  };

  const handleBanUserToolboxStatusUpdate = (participantId) => {
    const dataToPass = {
      participant_id: participantId,
    };
    adminUpdateUserToolboxBanStatusApiCall(
      dataToPass,
      roomModerators,
      roomId,
      currentUserIsAdmin
    );
  };

  const handleModeratorshipStatusUpdate = (participantId) => {
    const dataToPass = {
      participant_id: participantId,
    };
    adminUpdateUserModeratorshipStatusApiCall(
      dataToPass,
      roomModerators,
      roomId
    );
  };

  const handleKickOutVoteUpdate = (participantId) => {
    const dataToPass = {
      participant_id: participantId,
    };
    userKickOffParticipantVoteApiCall(dataToPass, roomId);
  };

  const openParticipantProfile = (username, event) => {
    event.stopPropagation();
    if (!username) return;
    window.open(`/${encodeURIComponent(username)}`, "_blank", "noopener,noreferrer");
  };

  const checkUserIsRoomAdminOrModerator = (moderatorId) => {
    const getModerator = roomModerators.find(
      (moderator) => moderator.id?.toString() === moderatorId?.toString()
    );
    return getModerator;
  };

  return (
    <div className={classes.userLobbyList}>
      <TransitionGroup>
        {roomLoading ? (
          <div>Loading...</div>
        ) : (
          roomParticipants?.map((participant, index) => {
            const participantRef = participantRefs.current[index];
            const currentParticipantId = participant?.id; // Added a safe navigation operator in case participant is undefined

            const getCurrentParticipantIsAdmin =
              checkUserIsRoomAdminOrModerator(currentParticipantId);

            return (
              <CSSTransition
                key={currentParticipantId} // Added a safe navigation operator in case participant is undefined
                nodeRef={participantRef}
                timeout={300}
                classNames="participant-transition"
                unmountOnExit
              >
                <div
                  className={
                    currentUserIsAdmin
                      ? participant?.is_banned
                        ? `${classes.userLobbyDisabledButton}`
                        : `${classes.userLobbyButton}`
                      : mutedActiveRoomParticipantIds[currentParticipantId]
                        ? `${classes.userLobbyDisabledButton}`
                        : `${classes.userLobbyButton}`
                  }
                  ref={participantRef}
                >
                  <div className={classes.userLobbyInfo}>
                    {capitalizeEachFirstWord(participant?.username)}

                    <div className={classes.userLobbyAdminInfo}>
                      {participant?.is_admin ? (
                        <span
                          style={{
                            fontSize: "12px",
                            color: "tomato",
                          }}
                        >
                          {participant?.is_admin?.toString() === "1"
                            ? "Admin"
                            : "Moderator"}
                        </span>
                      ) : null}
                      <span
                        style={{
                          fontSize: "9px",
                          color: "aqua",
                        }}
                      >
                        {currentParticipantId?.toString() === userId?.toString()
                          ? " (You)"
                          : null}
                      </span>
                    </div>
                  </div>
                  <div className={classes.userLobbyMoreIconButton}>
                    <span
                      className={
                        participant?.is_online
                          ? classes.onlineGreenDot
                          : classes.offlineRedDot
                      }
                      title={participant?.is_online ? "Online" : "Offline"}
                    ></span>

                    <IconButton
                      aria-label={`Open ${participant?.username} profile`}
                      className={classes.userLobbyProfileIconButton}
                      onClick={(event) => openParticipantProfile(participant?.username, event)}
                      title="Open profile in new tab"
                    >
                      <AccountCircleIcon style={{ color: "white" }} />
                    </IconButton>

                    {checkCurrentUserIsRoomModerator?.is_admin?.toString() ===
                      "1" &&
                      getCurrentParticipantIsAdmin?.is_admin?.toString() ===
                      "2" && (
                        <div>
                          <IconButton
                            aria-label="more options"
                            aria-controls="adminMenuOptions"
                            aria-haspopup="true"
                            onClick={handleAdminClick}
                          >
                            <MoreVertIcon style={{ color: "white" }} />
                          </IconButton>

                          <Menu
                            id="adminMenuOptions"
                            anchorEl={anchorAdminEl}
                            keepMounted
                            open={Boolean(anchorAdminEl)}
                            onClose={handleAdminClose}
                            TransitionComponent={Fade}
                            PaperProps={sharedMenuPaperProps}
                          >
                            <MenuItem
                              onClick={() =>
                                handleModeratorshipStatusUpdate(
                                  currentParticipantId
                                )
                              }
                            >
                              Remove Moderatorship
                            </MenuItem>
                          </Menu>
                        </div>
                      )}

                    {!participant?.is_admin &&
                      userId?.toString() !==
                      currentParticipantId?.toString() && (
                        <div>
                          <IconButton
                            aria-label="more options"
                            aria-controls="userMenuOptions"
                            aria-haspopup="true"
                            onClick={handleUserClick}
                          >
                            <MoreVertIcon style={{ color: "white" }} />
                          </IconButton>

                          <Menu
                            id="userMenuOptions"
                            anchorEl={anchorUserEl}
                            keepMounted
                            open={Boolean(anchorUserEl)}
                            onClose={handleUserClose}
                            TransitionComponent={Fade}
                            PaperProps={sharedMenuPaperProps}
                          >
                            <MenuItem
                              onClick={() =>
                                handleBanStatusUpdate(currentParticipantId)
                              }
                            >
                              {currentUserIsAdmin
                                ? participant?.is_banned
                                  ? `Unmute`
                                  : `Mute`
                                : mutedActiveRoomParticipantIds[currentParticipantId]
                                  ? `Unmute`
                                  : `Mute`}
                            </MenuItem>
                            <MenuItem
                              onClick={() =>
                                handleKickOutVoteUpdate(currentParticipantId)
                              }
                            >
                              {participant?.kickout_count >= 7
                                ? "Kicked Out"
                                : `Kick Out (${participant?.kickout_count})`}
                            </MenuItem>
                            <MenuItem
                              onClick={() =>
                                handleBanUserToolboxStatusUpdate(currentParticipantId)
                              }
                              disabled={!currentUserIsAdmin}
                            >
                              {participant?.is_toolbox_banned
                                ? `Activate Toolbox`
                                : `Deactivate Toolbox`}
                            </MenuItem>
                            <MenuItem
                              onClick={() =>
                                handleModeratorshipStatusUpdate(
                                  currentParticipantId
                                )
                              }
                              disabled={!currentUserIsAdmin}
                            >
                              Assign As Moderator
                            </MenuItem>
                          </Menu>
                        </div>
                      )}
                  </div>
                </div>
              </CSSTransition>
            );
          })
        )}
      </TransitionGroup>
    </div>
  );
}
